
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface DashboardStats {
  totalApplications: number;
  newMessages: number;
  demoRequests: number;
  activeProjects: number;
}

interface RecentApplication {
  id: string;
  name: string;
  email: string;
  date: string;
  status: 'new' | 'reviewing' | 'completed' | 'archived';
  sector: string;
  type: string;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalApplications: 0,
    newMessages: 0,
    demoRequests: 0,
    activeProjects: 0
  });
  const [recentApplications, setRecentApplications] = useState<RecentApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Supabase'den gerçek veri çek
      const [contactRes, demoRes, newsletterRes] = await Promise.all([
        supabase.from('contact_submissions').select('*'),
        supabase.from('demo_requests').select('*'),
        supabase.from('newsletter_subscriptions').select('*')
      ]);

      const totalApplications = (contactRes.data?.length || 0) + (demoRes.data?.length || 0) + (newsletterRes.data?.length || 0);
      const newMessages = (contactRes.data?.length || 0);
      const demoRequests = (demoRes.data?.length || 0);

      setStats({
        totalApplications,
        newMessages,
        demoRequests,
        activeProjects: Math.floor(totalApplications * 0.3) // %30'u aktif proje olarak hesapla
      });

      // Son 5 başvuruyu hazırla
      const allApplications = [
        ...(contactRes.data || []).map(item => ({
          id: item.id,
          name: item.name || 'İsimsiz',
          email: item.email,
          date: item.created_at,
          status: 'new' as const,
          sector: 'Genel',
          type: 'İletişim'
        })),
        ...(demoRes.data || []).map(item => ({
          id: item.id,
          name: item.name || 'İsimsiz',
          email: item.email,
          date: item.created_at,
          status: 'reviewing' as const,
          sector: 'Teknoloji',
          type: 'Demo Talebi'
        }))
      ];

      // Tarihe göre sırala ve son 5'ini al
      allApplications.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setRecentApplications(allApplications.slice(0, 5));

    } catch (error) {
      console.error('Dashboard verisi yüklenirken hata:', error);
      // Hata durumunda demo veriler
      setStats({
        totalApplications: 156,
        newMessages: 23,
        demoRequests: 8,
        activeProjects: 42
      });

      setRecentApplications([
        {
          id: '1',
          name: 'Ahmet Yılmaz',
          email: 'ahmet@example.com',
          date: '2025-01-08',
          status: 'new',
          sector: 'E-ticaret',
          type: 'Web Sitesi'
        },
        {
          id: '2',
          name: 'Fatma Kaya',
          email: 'fatma@example.com',
          date: '2025-01-08',
          status: 'reviewing',
          sector: 'Sağlık',
          type: 'Demo Talebi'
        },
        {
          id: '3',
          name: 'Mehmet Demir',
          email: 'mehmet@example.com',
          date: '2025-01-07',
          status: 'completed',
          sector: 'Eğitim',
          type: 'İletişim'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new': return 'Yeni';
      case 'reviewing': return 'İnceleniyor';
      case 'completed': return 'Tamamlandı';
      case 'archived': return 'Arşiv';
      default: return 'Bilinmiyor';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Dashboard yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Hoş geldin, Yönetici 👋</h1>
            <p className="text-blue-100">Bugün {new Date().toLocaleDateString('tr-TR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
          </div>
          <div className="hidden md:block">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-dashboard-3-line text-3xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <i className="ri-file-list-3-line text-xl text-blue-600"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats.totalApplications}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Toplam Başvuru</h3>
          <p className="text-sm text-gray-600">Bu ay +12% artış</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <i className="ri-message-3-line text-xl text-green-600"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats.newMessages}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Yeni Mesajlar</h3>
          <p className="text-sm text-gray-600">Yanıt bekliyor</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <i className="ri-presentation-line text-xl text-purple-600"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats.demoRequests}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Canlı Demo Talepleri</h3>
          <p className="text-sm text-gray-600">Bu hafta</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <i className="ri-rocket-line text-xl text-orange-600"></i>
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats.activeProjects}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Bu Ayın Aktif Projeleri</h3>
          <p className="text-sm text-gray-600">Devam ediyor</p>
        </div>
      </div>

      {/* Charts and Recent Applications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Chart Area */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Son 30 Gün Başvuru Grafiği</h2>
            <div className="flex space-x-2">
              <button className="px-3 py-1 text-sm bg-blue-100 text-blue-600 rounded-lg">Günlük</button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Haftalık</button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Aylık</button>
            </div>
          </div>
          
          {/* Simple Chart Placeholder */}
          <div className="h-64 bg-gradient-to-t from-blue-50 to-transparent rounded-xl flex items-end justify-center space-x-2 p-4">
            {[65, 45, 78, 52, 89, 67, 43, 76, 54, 82, 69, 91, 58, 73].map((height, index) => (
              <div
                key={index}
                className="bg-gradient-to-t from-blue-500 to-purple-500 rounded-t-lg w-6 transition-all duration-300 hover:from-blue-600 hover:to-purple-600"
                style={{ height: `${height}%` }}
              ></div>
            ))}
          </div>
          
          <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              Başvurular
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
              Demo Talepleri
            </div>
          </div>
        </div>

        {/* Sector Distribution */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-6">En Çok Gelen Sektörler</h2>
          
          <div className="space-y-4">
            {[
              { name: 'E-ticaret', percentage: 35, color: 'bg-blue-500' },
              { name: 'Sağlık', percentage: 25, color: 'bg-green-500' },
              { name: 'Eğitim', percentage: 20, color: 'bg-purple-500' },
              { name: 'Teknoloji', percentage: 15, color: 'bg-orange-500' },
              { name: 'Diğer', percentage: 5, color: 'bg-gray-400' }
            ].map((sector, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-700">{sector.name}</span>
                  <span className="text-gray-600">{sector.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${sector.color} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${sector.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Applications */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Son Başvurular</h2>
          <Link
            to="/admin/applications"
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-arrow-right-line mr-2"></i>
            Tümünü Görüntüle
          </Link>
        </div>

        {recentApplications.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Ad Soyad</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Tarih</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Durum</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Sektör</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Tür</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {recentApplications.map((application) => (
                  <tr key={application.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{application.name}</div>
                        <div className="text-sm text-gray-600">{application.email}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {new Date(application.date).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                        {getStatusText(application.status)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-600">{application.sector}</td>
                    <td className="py-4 px-4 text-gray-600">{application.type}</td>
                    <td className="py-4 px-4">
                      <div className="flex space-x-2">
                        <Link
                          to="/admin/applications"
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Görüntüle"
                        >
                          <i className="ri-eye-line"></i>
                        </Link>
                        <button 
                          className="w-8 h-8 flex items-center justify-center text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Düzenle"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
            <p className="text-gray-600">Henüz başvuru bulunmuyor</p>
            <Link
              to="/admin/applications"
              className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Başvuruları Görüntüle
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/admin/applications"
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <i className="ri-file-list-3-line text-xl text-blue-600"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Başvuruları Yönet</h3>
              <p className="text-sm text-gray-600">Gelen başvuruları incele</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/messages"
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <i className="ri-message-3-line text-xl text-green-600"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Mesajları Görüntüle</h3>
              <p className="text-sm text-gray-600">Müşteri mesajlarını yanıtla</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/customers"
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <i className="ri-team-line text-xl text-purple-600"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Müşteri Yönetimi</h3>
              <p className="text-sm text-gray-600">Müşteri bilgilerini düzenle</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
